import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTimer, TIMER_TYPES } from '../contexts/TimerContext';
import { useFormatTime } from '../hooks/useFormatTime';
import useSound from 'use-sound';

// Import sounds directly in this component
import workStartSound from '../assets/sounds/work-start.mp3';
import breakStartSound from '../assets/sounds/break-start.mp3';

import { 
  PlayIcon, 
  PauseIcon, 
  ArrowPathIcon, 
  ForwardIcon,
  CheckBadgeIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon
} from '@heroicons/react/24/solid';

export default function TomatoTimer() {
  const { 
    isRunning, 
    setIsRunning, 
    timerType, 
    setTimerType, 
    timeLeft, 
    setTimeLeft,
    resetTimer,
    completedPomodoros,
    setCompletedPomodoros,
    settings,
    saveSettings,
    forceLongBreakDuration
  } = useTimer();
  
  const formattedTime = useFormatTime(timeLeft);
  const [progress, setProgress] = useState(100);
  const [showLongBreakOptions, setShowLongBreakOptions] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [countdown, setCountdown] = useState(0); // New state for countdown
  
  // Sound hooks
  const [playWorkStart] = useSound(workStartSound, { volume: settings.soundVolume / 100 });
  const [playBreakStart] = useSound(breakStartSound, { volume: settings.soundVolume / 100 });
  
  // Calculate total time based on timer type
  const getTotalTime = () => {
    switch (timerType) {
      case TIMER_TYPES.WORK:
        return settings.workDuration * 60;
      case TIMER_TYPES.SHORT_BREAK:
        return settings.shortBreakDuration * 60;
      case TIMER_TYPES.LONG_BREAK:
        return settings.longBreakDuration * 60;
      default:
        return settings.workDuration * 60;
    }
  };

  // Update progress whenever timeLeft changes
  useEffect(() => {
    const totalTime = getTotalTime();
    const calculatedProgress = (timeLeft / totalTime) * 100;
    setProgress(calculatedProgress);
  }, [timeLeft, timerType, settings]);

  // Countdown effect
  useEffect(() => {
    let interval = null;
    
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prevCountdown => {
          if (prevCountdown <= 1) {
            // When countdown reaches 0, start the actual timer
            setIsRunning(true);
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [countdown, setIsRunning]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt+F to fast forward (for testing)
      if (e.altKey && e.key === 'f') {
        handleFastForward();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRunning]);

  // Get the appropriate colors based on timer type
  const getTimerColors = () => {
    switch (timerType) {
      case TIMER_TYPES.WORK:
        return {
          background: 'bg-tomato-100',
          fill: 'bg-tomato-600',
          text: 'text-tomato-700'
        };
      case TIMER_TYPES.SHORT_BREAK:
        return {
          background: 'bg-stem-100',
          fill: 'bg-stem-500',
          text: 'text-stem-700'
        };
      case TIMER_TYPES.LONG_BREAK:
        return {
          background: 'bg-sunny-100',
          fill: 'bg-sunny-500',
          text: 'text-amber-700'
        };
      default:
        return {
          background: 'bg-tomato-100',
          fill: 'bg-tomato-600',
          text: 'text-tomato-700'
        };
    }
  };

  const colors = getTimerColors();

  const handleToggleTimer = () => {
    // If showing options, clicking should select default option, not toggle timer
    if (showLongBreakOptions) {
      return;
    }
    
    // If we're going to start the timer and not currently in countdown mode
    if (!isRunning && countdown === 0) {
      // Start the countdown (changed from 5 to 3 seconds)
      setCountdown(3);
      
      // Play the appropriate sound when countdown starts
      if (settings.soundEnabled) {
        if (timerType === TIMER_TYPES.WORK) {
          playWorkStart();
        } else {
          playBreakStart();
        }
      }
    } else {
      // Otherwise just toggle the timer as usual
      setIsRunning(!isRunning);
    }
  };

  const handleResetTimer = () => {
    setIsRunning(false);
    setCountdown(0); // Reset countdown
    resetTimer();
  };

  const handleSkipTimer = () => {
    // Reset countdown if it's active
    if (countdown > 0) {
      setCountdown(0);
    }
    
    // Define what happens next based on current timer type
    if (timerType === TIMER_TYPES.WORK) {
      // Increment completed pomodoros when skipping from work to break
      const newCompletedPomodoros = completedPomodoros + 1;
      setCompletedPomodoros(newCompletedPomodoros);
      
      // Go to a long break after every 4th pomodoro (when completedPomodoros becomes 4, 8, etc)
      if (newCompletedPomodoros % settings.pomodorosUntilLongBreak === 0) {
        // Time for a long break - use current setting without asking
        forceLongBreakDuration(settings.longBreakDuration);
      } else {
        // Otherwise go to short break
        setTimerType(TIMER_TYPES.SHORT_BREAK);
      }
    } else if (timerType === TIMER_TYPES.SHORT_BREAK) {
      // If we're in a short break, go back to work mode
      setTimerType(TIMER_TYPES.WORK);
    } else if (timerType === TIMER_TYPES.LONG_BREAK) {
      // If we're in a long break, go back to work mode and reset counter
      setTimerType(TIMER_TYPES.WORK);
      // Reset the pomodoro counter after a long break
      setCompletedPomodoros(0);
    }
  };

  // Handle fast forward (testing feature)
  const handleFastForward = () => {
    // Force timer to exactly 1 second
    setTimeLeft(1);
    
    // Make sure the timer is running
    setIsRunning(true);
    
    console.log("Fast forward activated - timer set to 1 second");
  };

  // Toggle sound on/off
  const toggleSound = () => {
    const newSettings = {
      ...settings,
      soundEnabled: !settings.soundEnabled
    };
    saveSettings(newSettings);
  };

  const handleLongBreakDurationSelect = (duration) => {
    // This function will directly set the break duration and apply it immediately
    forceLongBreakDuration(duration);
    setShowLongBreakOptions(false);
    
    // Start the countdown instead of running directly (changed from 5 to 3 seconds)
    setCountdown(3);
    
    // Play the break start sound
    if (settings.soundEnabled) {
      playBreakStart();
    }
  };

  // Show long break options
  const showLongBreakSelection = () => {
    setShowLongBreakOptions(true);
    setIsRunning(false);
  };

  // Calculate progress to long break
  const progressToLongBreak = completedPomodoros % settings.pomodorosUntilLongBreak;
  const remainingToLongBreak = settings.pomodorosUntilLongBreak - progressToLongBreak;

  // Progress bar percentage
  const progressPercentage = (progressToLongBreak / settings.pomodorosUntilLongBreak) * 100;

  return (
    <div className="flex flex-col items-center" role="timer" aria-live="polite">
      {/* Timer Type Selector */}
      <div className="flex flex-wrap justify-center gap-2 mb-8" role="radiogroup" aria-label="Timer type">
        <button 
          className={`px-5 py-3 rounded-full text-base font-bold focus:outline-none focus:ring-2 focus:ring-tomato-500 focus:ring-offset-2 ${
            timerType === TIMER_TYPES.WORK 
              ? 'bg-tomato-600 text-white' 
              : 'bg-white text-tomato-600 border-2 border-tomato-600'
          }`}
          onClick={() => {
            setTimerType(TIMER_TYPES.WORK);
            setShowLongBreakOptions(false);
          }}
          aria-pressed={timerType === TIMER_TYPES.WORK}
          aria-label="Pomodoro focus timer"
        >
          Pomodoro
        </button>
        <button 
          className={`px-5 py-3 rounded-full text-base font-bold focus:outline-none focus:ring-2 focus:ring-stem-500 focus:ring-offset-2 ${
            timerType === TIMER_TYPES.SHORT_BREAK 
              ? 'bg-stem-500 text-white' 
              : 'bg-white text-stem-500 border-2 border-stem-500'
          }`}
          onClick={() => {
            setTimerType(TIMER_TYPES.SHORT_BREAK);
            setShowLongBreakOptions(false);
          }}
          aria-pressed={timerType === TIMER_TYPES.SHORT_BREAK}
          aria-label="Short break timer"
        >
          Short Break
        </button>
        <button 
          className={`px-5 py-3 rounded-full text-base font-bold focus:outline-none focus:ring-2 focus:ring-sunny-500 focus:ring-offset-2 ${
            timerType === TIMER_TYPES.LONG_BREAK && !showLongBreakOptions
              ? 'bg-sunny-500 text-white' 
              : 'bg-white text-sunny-500 border-2 border-sunny-500'
          }`}
          onClick={showLongBreakSelection}
          aria-pressed={timerType === TIMER_TYPES.LONG_BREAK && !showLongBreakOptions}
          aria-label="Long break timer"
        >
          Long Break
        </button>
      </div>

      {/* Pomodoro Definition Button */}
      <div className="relative mb-1">
        <button 
          onClick={() => setShowInfoModal(true)}
          className="absolute -top-2 right-2 z-10 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-md hover:bg-gray-50"
          aria-label="What is a Pomodoro?"
        >
          <QuestionMarkCircleIcon className="w-6 h-6 text-gray-500" />
        </button>

        {/* Pomodoro Timer with tomato image */}
        <div className="tomato-timer-container relative" style={{ width: '280px', height: '280px', margin: '0 auto' }}>
          
          {/* Tomato Image */}
          <div
            className="tomato-image-container w-full h-full cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={handleToggleTimer}
            style={{
              backgroundImage: 'url("/images/tomato.png")',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              width: '100%',
              height: '100%',
              position: 'relative',
              zIndex: 1
            }}
          >
            {/* Timer Fill Animation - semi-transparent overlay that fills from bottom */}
            <motion.div 
              className="absolute bottom-0 left-0 right-0 z-2"
              style={{ 
                height: `${100 - progress}%`,
                opacity: 0, // Hide the fill animation
                transition: 'height 0.3s linear'
              }}
              initial={{ height: '0%' }}
              animate={{ height: `${100 - progress}%` }}
              transition={{ duration: 0.5 }}
            />

            {/* Timer Display */}
            {showLongBreakOptions ? (
              <div className="absolute inset-0 flex items-center justify-center" style={{ paddingTop: '40px' }}>
                <div className="bg-white bg-opacity-80 py-4 px-6" style={{ borderRadius: '16px', width: '80%', maxWidth: '220px' }}>
                  <h3 className="text-2xl font-bold mb-2 text-amber-700 text-center">Choose<br/>Break<br/>Length</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {[10, 15, 20, 25, 30, 45].map((duration) => (
                      <button
                        key={duration}
                        className={`p-2 rounded-lg text-base font-bold ${
                          settings.longBreakDuration === duration
                            ? 'bg-amber-500 text-white'
                            : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                        }`}
                        onClick={() => handleLongBreakDurationSelect(duration)}
                        style={{ minWidth: '60px' }}
                      >
                        {duration}<br/>min
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : countdown > 0 ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '40px' }}>
                <div className="text-center bg-white bg-opacity-70 px-6 py-4 rounded-full" style={{ width: '80%', maxWidth: '220px' }}>
                  <div className="animate-pulse mb-2">
                    <h2 className="text-6xl md:text-7xl font-bold text-red-800" style={{ lineHeight: 1.1 }}>{countdown}</h2>
                  </div>
                  <p className="text-lg md:text-xl font-semibold text-red-800">
                    {timerType === TIMER_TYPES.WORK 
                      ? 'Starting Focus Session...' 
                      : timerType === TIMER_TYPES.SHORT_BREAK 
                        ? 'Starting Short Break...' 
                        : 'Starting Long Break...'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '40px' }}>
                <div 
                  className="text-center bg-white bg-opacity-70 px-6 py-4 rounded-full" 
                  style={{ width: '80%', maxWidth: '220px' }}
                  onDoubleClick={handleFastForward} // Add double-click handler
                >
                  <h2 className="text-5xl md:text-6xl font-bold text-red-800" style={{ lineHeight: 1.1 }}>{formattedTime}</h2>
                  <p className="text-lg md:text-xl font-semibold mt-1 text-red-800">
                    {timerType === TIMER_TYPES.WORK 
                      ? 'Focus Time' 
                      : timerType === TIMER_TYPES.SHORT_BREAK 
                        ? 'Short Break' 
                        : `Long Break (${settings.longBreakDuration} min)`}
                  </p>
                  
                  {/* Completed Pomodoros Indicator */}
                  <div className="flex justify-center space-x-2 mt-2 md:mt-3">
                    {Array.from({ length: settings.pomodorosUntilLongBreak }).map((_, index) => (
                      <div 
                        key={index}
                        className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${
                          index < progressToLongBreak 
                            ? 'bg-white' 
                            : 'bg-white bg-opacity-50'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timer Controls */}
      <div className="timer-controls flex items-center justify-center space-x-3 md:space-x-6">
        <button 
          className="btn-circle-large hover:bg-tomato-50"
          onClick={handleResetTimer}
          aria-label="Reset timer"
        >
          <ArrowPathIcon className="control-icon-small" />
        </button>
        
        <button 
          className={`btn-circle-xl hover:bg-tomato-50 ${showLongBreakOptions ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={handleToggleTimer}
          aria-label={isRunning ? "Pause timer" : "Start timer"}
          disabled={showLongBreakOptions}
        >
          {isRunning ? (
            <PauseIcon className="control-icon-large" />
          ) : (
            <PlayIcon className="control-icon-large" />
          )}
        </button>
        
        <button 
          className="btn-circle-large hover:bg-tomato-50"
          onClick={handleSkipTimer}
          aria-label="Skip to next timer"
        >
          <ForwardIcon className="control-icon-small" />
        </button>

        <button 
          className="btn-circle-medium hover:bg-tomato-50 ml-2"
          onClick={toggleSound}
          aria-label={settings.soundEnabled ? "Mute sound" : "Unmute sound"}
        >
          {settings.soundEnabled ? (
            <SpeakerWaveIcon className="control-icon-small" />
          ) : (
            <SpeakerXMarkIcon className="control-icon-small" />
          )}
        </button>
        
        {/* Hidden button for fast forward - only visible in development mode and when explicitly enabled */}
        {false && (
          <button 
            className="btn-circle-medium hover:bg-tomato-50 ml-2 bg-gray-100 text-gray-400"
            onClick={handleFastForward}
            aria-label="Fast Forward (Testing Only)"
            title="Fast Forward (Testing Only)"
          >
            <div className="text-xs font-bold">FF</div>
          </button>
        )}
      </div>

      {/* Progress Tracker - Professional Version */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="progress-card max-w-md"
      >
        <div className="px-6 py-5">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-tomato-600 flex items-center justify-center mr-3">
                <CheckBadgeIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Pomodoro Progress</h3>
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-tomato-600">{progressToLongBreak}</span>
              <span className="text-xl text-gray-400 font-medium">/{settings.pomodorosUntilLongBreak}</span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="progress-bar">
            <div 
              className="progress-bar-fill"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          
          <p className="mt-3 text-gray-600 font-medium">
            {remainingToLongBreak === 0 
              ? "Time for a long break!" 
              : remainingToLongBreak === 1
                ? "1 more until long break"
                : `${remainingToLongBreak} more until long break`}
          </p>
        </div>
      </motion.div>

      {/* Pomodoro Definition Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md shadow-xl"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-tomato-600">What is a Pomodoro?</h3>
              <button 
                onClick={() => setShowInfoModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <p className="mb-4">
              A <strong>pomodoro</strong> is defined as 25 minutes of focused work plus a 5-minute break. The term comes from the Italian word for tomato, inspired by the tomato-shaped kitchen timer.
            </p>
            
            <div className="flex flex-col md:flex-row gap-6 mb-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 flex-1">
                <h4 className="font-bold mb-2">1 × Pomodoro =</h4>
                <p>25 mins focus + 5 min break</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 flex-1">
                <h4 className="font-bold mb-2">4 × Pomodori</h4>
                <p>+ longer break (10-45 minutes)</p>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <h4 className="font-bold mb-2">The Pomodoro Cycle:</h4>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Complete one pomodoro (25 min focus + 5 min break)</li>
                <li>Repeat three more times</li>
                <li>After 4 pomodoros, take a longer break</li>
              </ol>
            </div>
            
            <button
              onClick={() => setShowInfoModal(false)}
              className="mt-5 w-full py-2 bg-tomato-600 text-white rounded-md hover:bg-tomato-700 transition-colors"
            >
              Got it!
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
